import type { IdentificadorLoja } from '../../../api';

/**
 * Medidas de cada aparelho, num lugar só.
 *
 * Os componentes de `components/catalogo-kiosk/` são compartilhados por três
 * telas — totem de checkout, totem de pedidos e tablet — e já aconteceu de um
 * ajuste feito para o tablet mudar a grade do checkout, que está em produção.
 * Por isso o tamanho NÃO mora mais dentro do componente: o padrão dele é o do
 * checkout (e, desde a padronização, também o do totem de pedidos), e só o
 * tablet pede medidas próprias aqui.
 *
 * Mexer numa linha desta tabela só afeta o aparelho daquela linha.
 */
export interface MedidasKiosk {
  larguraLateral: string;
  /**
   * Altura MÍNIMA do botão de categoria. Quem manda de fato é o conteúdo
   * (`height: auto` no botão), então para deixar a lateral compacta é o ícone e
   * a fonte abaixo que precisam encolher — mexer só aqui não muda nada.
   */
  alturaCategoria: string;
  /** Lado do ícone da categoria, em px. */
  tamanhoIcone: number;
  /** Tamanho da fonte do nome da categoria. */
  fonteCategoria: string;
  larguraMinimaCard: string;
  /** Fonte do nome do grupo acima da grade. Só o tablet em pé pede: lá ele divide a linha com o "Buscar" numa grade estreita. */
  fonteTituloGrade?: string;
  /** Como a lateral avisa que a lista continua abaixo. */
  indicadorRolagem: 'botao' | 'esmaecer';
  /** Altura mínima de "Todos" e "Destaques" — igual à da categoria mantém a lista uniforme. */
  alturaAtalho: string;
  /** Altura máxima do logo no cabeçalho do carrinho e do fechamento do pedido, em px. */
  alturaLogo: number;
  /** Fonte do título e do subtítulo desse mesmo cabeçalho. */
  fonteTitulo: string;
  fonteSubtitulo: string;
  /**
   * Cartão estreito: com item no carrinho, seletor de quantidade e preço encolhem para caber na
   * mesma linha; estreito demais até para isso (tablet em pé), o botão desce para baixo do preço.
   */
  seletorCompacto: boolean;
}

/**
 * Tablet na mesa, DEITADO — o caso normal, já que o `KioskDispositivo` trava o
 * tablet em paisagem.
 *
 * É o perfil mais apertado dos dois, e o motivo é o espaço vertical: deitado a
 * tela é larga e BAIXA, então a lista de categorias tem pouca altura para
 * ocupar. Largura sobra, e é por isso que a lateral pode ser mais larga aqui —
 * nome que não quebra em duas linhas é altura que não se perde.
 */
const TABLET_DEITADO: MedidasKiosk = {
  larguraLateral: 'clamp(180px, 24%, 280px)',
  alturaCategoria: 'clamp(60px, 9vh, 80px)',
  alturaAtalho: 'clamp(60px, 9vh, 80px)',
  tamanhoIcone: 34,
  alturaLogo: 44,
  fonteTitulo: '1.3rem',
  fonteSubtitulo: '0.95rem',
  seletorCompacto: true,
  fonteCategoria: '0.9rem',
  larguraMinimaCard: 'clamp(190px, 20vw, 250px)',
  indicadorRolagem: 'esmaecer',
};

/**
 * Tablet EM PÉ. Não deveria acontecer — o kiosk pede
 * `screen.orientation.lock('landscape')` —, mas a trava é best-effort: só vale
 * em tela cheia e nem todo navegador atende.
 *
 * Aqui a conta se inverte: a tela é alta, então há altura de sobra para a
 * lista e o item pode ser maior. O que fica escasso é a largura, que a lateral
 * divide com a grade. A lateral fica com perto de um terço — com 150px os nomes
 * das categorias quebravam em duas linhas — e a grade mostra DOIS cartões por
 * linha: com um só, a foto tomava a tela e o cliente via um produto por vez.
 */
const TABLET_EM_PE: MedidasKiosk = {
  larguraLateral: 'clamp(180px, 30%, 250px)',
  alturaCategoria: 'clamp(76px, 8vh, 100px)',
  alturaAtalho: 'clamp(76px, 8vh, 100px)',
  tamanhoIcone: 42,
  alturaLogo: 44,
  fonteTitulo: '1.3rem',
  fonteSubtitulo: '0.95rem',
  seletorCompacto: true,
  fonteCategoria: '1rem',
  larguraMinimaCard: 'clamp(150px, 26vw, 220px)',
  fonteTituloGrade: '1.5rem',
  indicadorRolagem: 'esmaecer',
};

const TOTEM: Partial<MedidasKiosk> = {};

/**
 * @param emPe tela em retrato. Só muda o tablet: o totem já é feito para
 *   retrato, e o de checkout não usa esta tabela.
 */
export function medidasDoAparelho(identificador: IdentificadorLoja, emPe = false): Partial<MedidasKiosk> {
  if (identificador.tipo !== 'TABLET') return TOTEM;
  return emPe ? TABLET_EM_PE : TABLET_DEITADO;
}
